import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, Clock, Tag as TagIcon, Loader2, Rss } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

function PostCard({ post }) {
  return (
    <article
      data-testid={`blog-card-${post.slug}`}
      className="bg-[#1A1715] border border-[#F5F2EB]/10 hover:border-[#D95B38]/40 transition-colors group"
    >
      <Link to={`/blog/${post.slug}`} className="block">
        {post.cover_image ? (
          <div className="aspect-[16/9] overflow-hidden bg-[#0F0D0C] border-b border-[#F5F2EB]/10">
            <img
              src={post.cover_image}
              alt={post.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          </div>
        ) : (
          <div className="aspect-[16/9] bg-gradient-to-br from-[#1A1715] to-[#2B2624] border-b border-[#F5F2EB]/10 flex items-center justify-center">
            <span className="font-serif italic text-3xl text-[#D95B38]/30">AgentReply</span>
          </div>
        )}
        <div className="p-6 md:p-7">
          {post.tags?.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {post.tags.slice(0, 3).map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D95B38] border border-[#D95B38]/30 px-2 py-1">
                  {t}
                </span>
              ))}
            </div>
          )}
          <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3 group-hover:text-[#D95B38] transition-colors leading-tight">
            {post.title}
          </h3>
          <p className="font-mono text-sm text-[#A39A8F] leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A7068]">
            <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" strokeWidth={1.5} />{fmtDate(post.published_at || post.created_at)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" strokeWidth={1.5} />{post.reading_time_minutes || 1} min read</span>
          </div>
        </div>
      </Link>
    </article>
  );
}

export function BlogIndex() {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/blog/posts?limit=50")
      .then((r) => setPosts(r.data.items || []))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load posts"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Helmet>
        <title>Blog · AgentReply — AI agent tips, WhatsApp guides &amp; product updates</title>
        <meta name="description" content="AI agent tips, WhatsApp marketing guides, customer support automation playbooks and AgentReply product updates. Read the latest from the AgentReply blog." />
        <link rel="canonical" href="https://agentreply.io/blog" />
        <link rel="alternate" type="application/rss+xml" title="AgentReply Blog RSS" href="https://agentreply.io/api/blog/rss.xml" />
        <meta property="og:title" content="AgentReply Blog · AI agent tips &amp; WhatsApp guides" />
        <meta property="og:description" content="AI agent tips, WhatsApp marketing guides and AgentReply product updates." />
        <meta property="og:url" content="https://agentreply.io/blog" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "AgentReply Blog",
          "url": "https://agentreply.io/blog",
          "description": "AI agent tips, WhatsApp marketing guides and AgentReply product updates.",
          "blogPost": posts.slice(0, 10).map((p) => ({
            "@type": "BlogPosting",
            "headline": p.title,
            "url": `https://agentreply.io/blog/${p.slug}`,
            "datePublished": p.published_at,
            "image": p.cover_image || undefined,
            "author": { "@type": "Person", "name": p.author || "AgentReply Team" },
          })),
        })}</script>
      </Helmet>
      <Navbar user={user} onLogout={logout} />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="blog-index">
        <header className="max-w-3xl mb-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4 flex items-center gap-2">
            <Rss className="w-3 h-3" strokeWidth={2} />The AgentReply Journal
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-6 leading-[1.05]">
            Playbooks, guides &amp; product notes.
          </h1>
          <p className="font-mono text-base text-[#A39A8F] leading-relaxed mb-6">
            Practical tips on building AI agents that actually convert — WhatsApp marketing tactics, Instagram DM automations,
            knowledge-base patterns and the engineering choices behind AgentReply.
          </p>
          <a
            href="/api/blog/rss.xml"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blog-rss-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] transition-colors border border-[#F5F2EB]/15 hover:border-[#D95B38]/40 px-3 py-2"
          >
            <Rss className="w-3 h-3" strokeWidth={2} /> Subscribe via RSS
          </a>
        </header>

        {loading && (
          <div className="flex items-center gap-3 text-[#A39A8F] font-mono text-sm" data-testid="blog-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading posts…
          </div>
        )}
        {error && (
          <div className="bg-[#2B1815] border border-[#D95B38]/40 p-6 font-mono text-sm text-[#E0D8CA]" data-testid="blog-error">
            {error}
          </div>
        )}
        {!loading && !error && posts.length === 0 && (
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-10 text-center font-mono text-sm text-[#A39A8F]" data-testid="blog-empty">
            <p className="mb-2 text-[#F5F2EB] text-base">No posts yet — the journal is warming up.</p>
            <p>Check back in a few days, or <Link to="/help" className="text-[#D95B38] underline">read the Help Center</Link> in the meantime.</p>
          </div>
        )}
        {!loading && !error && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="blog-grid">
            {posts.map((p) => <PostCard key={p.slug} post={p} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { user, logout } = useAuth();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true); setPost(null); setError("");
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Post not found" : (e?.response?.data?.detail || "Could not load post")))
      .finally(() => setLoading(false));
    api.get("/blog/posts?limit=20")
      .then((r) => setRelated((r.data.items || []).filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Navbar user={user} onLogout={logout} />
      {post && (
        <Helmet>
          <title>{`${post.title} · AgentReply Blog`}</title>
          <meta name="description" content={post.meta_description || post.excerpt} />
          <link rel="canonical" href={post.canonical_url || `https://agentreply.io/blog/${post.slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.meta_description || post.excerpt} />
          <meta property="og:url" content={`https://agentreply.io/blog/${post.slug}`} />
          {post.cover_image && <meta property="og:image" content={post.cover_image} />}
          <meta property="article:published_time" content={post.published_at || post.created_at} />
          <meta property="article:author" content={post.author} />
          {post.tags?.map((t) => <meta key={t} property="article:tag" content={t} />)}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={post.meta_description || post.excerpt} />
          {post.cover_image && <meta name="twitter:image" content={post.cover_image} />}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.meta_description || post.excerpt,
            "image": post.cover_image || undefined,
            "datePublished": post.published_at,
            "dateModified": post.updated_at,
            "author": { "@type": "Person", "name": post.author || "AgentReply Team" },
            "publisher": { "@type": "Organization", "name": "AgentReply", "logo": { "@type": "ImageObject", "url": "https://agentreply.io/logo512.png" } },
            "mainEntityOfPage": `https://agentreply.io/blog/${post.slug}`,
            "keywords": (post.tags || []).join(", "),
          })}</script>
        </Helmet>
      )}
      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="blog-post">
        <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] transition-colors mb-12" data-testid="blog-back">
          <ArrowLeft className="w-3 h-3" strokeWidth={2} /> All posts
        </Link>

        {loading && (
          <div className="flex items-center gap-3 text-[#A39A8F] font-mono text-sm"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        )}
        {error && (
          <div className="bg-[#2B1815] border border-[#D95B38]/40 p-6 font-mono text-sm text-[#E0D8CA]" data-testid="blog-post-error">
            {error} — <Link to="/blog" className="text-[#D95B38] underline">browse all posts</Link>.
          </div>
        )}

        {post && (
          <article>
            {post.tags?.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-6">
                {post.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D95B38] border border-[#D95B38]/30 px-2 py-1 flex items-center gap-1">
                    <TagIcon className="w-3 h-3" strokeWidth={1.5} />{t}
                  </span>
                ))}
              </div>
            )}
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] mb-6" data-testid="blog-post-title">{post.title}</h1>
            <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8F] mb-10 pb-10 border-b border-[#F5F2EB]/10">
              <span>{post.author || "AgentReply Team"}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" strokeWidth={1.5} />{fmtDate(post.published_at || post.created_at)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" strokeWidth={1.5} />{post.reading_time_minutes || 1} min read</span>
            </div>

            {post.cover_image && (
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-auto mb-12 border border-[#F5F2EB]/10"
                loading="eager"
              />
            )}

            <div
              className="blog-content font-serif text-[18px] leading-[1.75] text-[#E0D8CA]"
              data-testid="blog-post-content"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
            />

            <div className="mt-16 pt-10 border-t border-[#F5F2EB]/10">
              <div className="bg-[#1A1715] border border-[#D95B38]/30 p-8 md:p-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">Try AgentReply</div>
                <h3 className="font-serif text-2xl md:text-3xl mb-3 leading-tight">Build your own AI agent — free for 14 days.</h3>
                <p className="font-mono text-sm text-[#A39A8F] mb-6 leading-relaxed">No card required. Live on WhatsApp, Instagram and the web in under 5 minutes.</p>
                <Link to="/signup" className="inline-block font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press" data-testid="blog-cta-signup">
                  Start free trial →
                </Link>
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-20">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-6">Keep reading</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/blog/${r.slug}`} className="block bg-[#1A1715] border border-[#F5F2EB]/10 hover:border-[#D95B38]/40 transition-colors p-5">
                      <div className="font-serif text-lg text-[#F5F2EB] mb-2 leading-snug">{r.title}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A7068]">{fmtDate(r.published_at)}</div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default BlogIndex;
